import { htmlToMarkdown } from "@blog/editor";
import { useRef, useState, type ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  statusVariant,
} from "@blog/ui";
import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { describeError } from "../lib/api-error";
import {
  extractTitleFromHtml,
  parseDocx,
  replaceDataImages,
  stripLeadingTitleHeading,
} from "../lib/docx-import";
import {
  buildRows,
  countByStatus,
  filterRows,
  sortRows,
  type ArticleRow,
  type SortKey,
} from "../lib/article-list";
import { formatDate } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";
import { deriveSlugFromTitle, fallbackSlug } from "../lib/slug";

/**
 * Article list (TASK-802, SPEC §40).
 *
 * One row per localization, not per entry: an article with a Chinese and an
 * English version is two things an editor works on, and collapsing them would
 * hide the one that is still a draft.
 *
 * Filtering and sorting happen in the browser over a page of results. That is
 * the right trade at this scale — the server would need a query language to do
 * the same thing, and a CMS with a few hundred articles does not.
 */
export function ArticleList() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [locale, setLocale] = useState("");
  const [sort, setSort] = useState<SortKey>("updated");

  const siteId = site?.id;

  const data = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listEntries(siteId, { type: "post", limit: 200 });
  }, [siteId]);

  if (siteLoading || data.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (data.error !== null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-red-600">{data.error}</p>
        </CardContent>
      </Card>
    );
  }

  const rows = data.data === null ? [] : buildRows(data.data.entries, data.data.localizations);
  const counts = countByStatus(rows);
  const filtered = filterRows(rows, { search, status, locale });
  const sorted = sortRows(filtered, sort, sort === "title" ? "asc" : "desc");
  const supportedLocales = site?.supportedLocales ?? [];

  /** Locales that already exist for an entry — a translation targets the rest. */
  const localesOf = (entryId: string): readonly string[] =>
    rows.filter((row) => row.entry.id === entryId).map((row) => row.localization.locale);

  return (
    <>
      <PageHeader
        title={t("articles.title")}
        description={t("articles.countDesc", { count: rows.length })}
      >
        <WordImport />
        <Link to="/articles/new">
          <Button size="sm">{t("articles.newArticle")}</Button>
        </Link>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder={t("articles.search")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />

        <select
          className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
          }}
          aria-label={t("articles.filterByStatus")}
        >
          <option value="">{t("articles.allStatuses")}</option>
          {Object.entries(counts).map(([key, count]) => (
            <option key={key} value={key}>
              {key} ({String(count)})
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm"
          value={locale}
          onChange={(event) => {
            setLocale(event.target.value);
          }}
          aria-label={t("articles.filterByLocale")}
        >
          <option value="">{t("articles.allLocales")}</option>
          {(site?.supportedLocales ?? []).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <select
          className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-sm"
          value={sort}
          onChange={(event) => {
            setSort(event.target.value as SortKey);
          }}
          aria-label={t("articles.sortBy")}
        >
          <option value="updated">{t("articles.sortUpdated")}</option>
          <option value="published">{t("articles.sortPublished")}</option>
          <option value="title">{t("articles.sortTitle")}</option>
          <option value="status">{t("articles.sortStatus")}</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <EmptyState
          message={
            rows.length === 0 ? t("articles.noArticles") : t("articles.noMatch")
          }
          hint={
            rows.length === 0
              ? t("articles.emptyHint")
              : undefined
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.title")}</TableHead>
                <TableHead>{t("common.locale")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("common.updated")}</TableHead>
                <TableHead>{t("common.path")}</TableHead>
                <TableHead>{t("articles.thTranslation")}</TableHead>
                <TableHead>{t("articles.thActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={`${row.entry.id}:${row.localization.locale}`}>
                  <TableCell>
                    <EditableTitle row={row} onSaved={() => data.reload()} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.localization.locale}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(row.localization.status)}>
                      {row.localization.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-neutral-500">
                    {formatDate(row.localization.updatedAt)}
                  </TableCell>
                  <TableCell className="text-xs text-neutral-400">
                    {row.localization.contentPath}
                  </TableCell>
                  <TableCell>
                    <AddTranslation
                      row={row}
                      existingLocales={localesOf(row.entry.id)}
                      supportedLocales={supportedLocales}
                      onCreated={() => {
                        data.reload();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <DeleteArticle
                      row={row}
                      localeCount={localesOf(row.entry.id).length}
                      onDeleted={() => {
                        data.reload();
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}

/**
 * The title cell: a link to the editor, plus an inline rename.
 *
 * A rename writes two rows, because the title lives in two places: the
 * localization (what the list shows) and the draft's frontmatter (what a
 * publish serializes). Updating only one would let the old title silently
 * win the next time the article is published — the draft would carry it
 * back. The draft is only touched when it exists; an article never saved
 * has nothing to keep in step.
 */
function EditableTitle({
  row,
  onSaved,
}: {
  readonly row: ArticleRow;
  readonly onSaved: () => void;
}) {
  const client = useApi();
  const { site } = useSite(client);
  const siteId = site?.id;

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function start(): void {
    setValue(row.localization.title);
    setError(null);
    setEditing(true);
  }

  async function save(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    const title = value.trim();
    const loc = row.localization;

    if (title === "") {
      setEditing(false);
      return;
    }

    if (title === loc.title) {
      setEditing(false);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await client.saveLocalization(siteId, row.entry.id, loc.locale, {
        title,
        slug: loc.slug,
        ...(loc.description === undefined ? {} : { description: loc.description }),
        status: loc.status,
        ...(loc.publishedAt === undefined ? {} : { publishedAt: loc.publishedAt }),
        ...(loc.scheduledAt === undefined ? {} : { scheduledAt: loc.scheduledAt }),
        contentPath: loc.contentPath,
      });

      const draft = await client.getDraft(siteId, row.entry.id, loc.locale);

      if (draft !== null) {
        await client.saveDraft(siteId, row.entry.id, loc.locale, {
          content: draft.content,
          frontmatter: { ...draft.frontmatter, title },
          ...(draft.baseGithubSha === undefined ? {} : { baseGithubSha: draft.baseGithubSha }),
        });
      }

      setEditing(false);
      onSaved();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Input
            className="h-8 max-w-60 text-sm"
            value={value}
            autoFocus
            disabled={busy}
            aria-label={t("articles.titleEditAria", { title: row.localization.title })}
            onChange={(event) => {
              setValue(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                void save();
              } else if (event.key === "Escape") {
                setEditing(false);
              }
            }}
          />
          <Button size="sm" variant="outline" loading={busy} onClick={() => void save()}>
            {t("common.save")}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
            {t("common.cancel")}
          </Button>
        </div>
        {error !== null && (
          <span role="alert" className="text-xs text-red-600">
            {t("articles.titleSaveFailed")}: {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {/* Long titles truncate with an ellipsis; the full text stays one hover away. */}
      <Link
        className="max-w-72 truncate font-medium text-neutral-900 hover:underline"
        title={row.localization.title}
        to={`/articles/${row.entry.id}/${row.localization.locale}`}
      >
        {row.localization.title}
      </Link>
      <button
        type="button"
        title={t("articles.titleEdit")}
        aria-label={t("articles.titleEditAria", { title: row.localization.title })}
        className="shrink-0 text-neutral-300 transition-colors hover:text-neutral-700"
        onClick={start}
      >
        <IconPencil />
      </button>
    </span>
  );
}

function IconPencil() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/**
 * Import a Word document as a new draft (docx -> Markdown).
 *
 * Everything happens in the browser: mammoth parses the .docx into HTML, every
 * embedded image is uploaded through the media API (bytes to Git, AGENTS.md
 * §13), and the editor package's own pipeline turns the HTML into the same
 * Markdown a typed draft would carry. The server sees only ordinary API calls.
 *
 * The entry is created up front rather than lazily, because image uploads need
 * an entry id — media lives under `assets/posts/{entryId}/` (SPEC §17).
 */
function WordImport() {
  const client = useApi();
  const navigate = useNavigate();
  const { site } = useSite(client);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [phase, setPhase] = useState<"idle" | "parsing" | "uploading" | "saving">("idle");
  const [error, setError] = useState<string | null>(null);

  const busy = phase !== "idle";

  const phaseText =
    phase === "parsing"
      ? t("articles.importParsing")
      : phase === "uploading"
        ? t("articles.importUploading")
        : t("articles.importSaving");

  async function onFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = "";

    const siteId = site?.id;

    if (file === undefined || siteId === undefined) {
      return;
    }

    if (!/\.docx$/i.test(file.name)) {
      setError(t("articles.importNotDocx"));
      return;
    }

    setError(null);
    setPhase("parsing");

    try {
      const bytes = await file.arrayBuffer();
      const { html } = await parseDocx(bytes);

      // The first heading is the title; a document without one falls back to
      // its filename. The slug follows the same rules as the editor's.
      const title = extractTitleFromHtml(html) ?? file.name.replace(/\.docx$/i, "");
      const slug = deriveSlugFromTitle(title, fallbackSlug(new Date()));
      const locale = site?.defaultLocale ?? "zh-CN";

      const entry = await client.createEntry(siteId, {
        type: "post",
        canonicalLocale: locale,
        contentPath: `posts/${slug}`,
      });

      await client.saveLocalization(
        siteId,
        entry.id,
        locale,
        {
          title,
          slug,
          status: "draft",
          contentPath: `posts/${slug}/${locale}.md`,
        },
        { create: true },
      );

      setPhase("uploading");
      // The first heading already became the title; it must not also open the
      // body, or every imported article would start with its own name twice.
      const bodyHtml = stripLeadingTitleHeading(html, title);
      const { html: withUploadedImages, failed } = await replaceDataImages(
        bodyHtml,
        async (image) => {
          const result = await client.uploadMedia(siteId, entry.id, image);
          return result.url;
        },
      );

      setPhase("saving");
      const markdown = htmlToMarkdown(withUploadedImages);

      await client.saveDraft(siteId, entry.id, locale, {
        content: markdown,
        frontmatter: { title, slug, status: "draft" },
      });

      void navigate(
        `/articles/${entry.id}/${locale}`,
        failed === 0 ? undefined : { state: { notice: t("articles.importImagesFailed", { count: failed }) } },
      );
    } catch (cause) {
      setError(describeError(cause));
      setPhase("idle");
    } finally {
      setPhase("idle");
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <input
        ref={inputRef}
        type="file"
        accept=".docx"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          void onFile(event);
        }}
      />
      <Button
        size="sm"
        variant="outline"
        loading={busy}
        aria-label={t("articles.importAria")}
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        {busy ? phaseText : t("articles.importWord")}
      </Button>
      {error === null ? null : (
        <span role="alert" className="text-xs text-red-600">
          {t("articles.importFailed")}: {error}
        </span>
      )}
    </div>
  );
}

/**
 * Delete one localization of an entry, from the list row.
 *
 * Semantics follow the server's:
 *
 *   - A **published** localization is unpublished first (TASK-504) — that is
 *     the explicit operation that stops the blog rendering it. The committed
 *     file stays in the repository, exactly as unpublish leaves it.
 *   - Deleting the **last** localization takes the whole entry with it
 *     (the API cascades localizations and drafts); any other deletion leaves
 *     the sibling translations alone.
 *
 * The confirmation spells out the published case, because that click reaches
 * further than the row in front of the user.
 */
function DeleteArticle({
  row,
  localeCount,
  onDeleted,
}: {
  readonly row: ArticleRow;
  readonly localeCount: number;
  readonly onDeleted: () => void;
}) {
  const client = useApi();
  const { site } = useSite(client);
  const siteId = site?.id;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    const published = row.localization.status === "published";
    const params = { title: row.localization.title, locale: row.localization.locale };
    const confirmed = window.confirm(
      published ? t("articles.deleteConfirmPublished", params) : t("articles.deleteConfirmDraft", params),
    );

    if (!confirmed) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      if (published) {
        await client.unpublish(siteId, row.entry.id, row.localization.locale);
      }

      await client.deleteLocalization(siteId, row.entry.id, row.localization.locale);

      if (localeCount <= 1) {
        await client.deleteEntry(siteId, row.entry.id);
      }

      onDeleted();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="ghost"
        className="text-red-600 hover:bg-red-50 hover:text-red-700"
        disabled={busy}
        aria-label={t("articles.deleteAria", { title: row.localization.title })}
        onClick={() => {
          void remove();
        }}
      >
        {t("articles.delete")}
      </Button>
      {error !== null && (
        <span role="alert" className="text-xs text-red-600">
          {t("articles.deleteFailed")}: {error}
        </span>
      )}
    </div>
  );
}

/**
 * Create a missing localization of an entry and open it in the editor
 * (TASK-805).
 *
 * The translation carries the source's title, slug and status so it starts as
 * a faithful copy; making it a real translation is the editor's job.
 */
function AddTranslation({
  row,
  existingLocales,
  supportedLocales,
  onCreated,
}: {
  readonly row: ArticleRow;
  readonly existingLocales: readonly string[];
  readonly supportedLocales: readonly string[];
  readonly onCreated: () => void;
}) {
  const client = useApi();
  const navigate = useNavigate();
  const { site } = useSite(client);
  const siteId = site?.id;

  const [locale, setLocale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missing = supportedLocales.filter((item) => !existingLocales.includes(item));

  if (missing.length === 0) {
    return <span className="text-xs text-neutral-400">—</span>;
  }

  async function add(): Promise<void> {
    if (siteId === undefined || locale === "") {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await client.saveLocalization(
        siteId,
        row.entry.id,
        locale,
        {
          title: row.localization.title,
          slug: row.localization.slug,
          status: "draft",
          contentPath: `${row.entry.contentPath}/${locale}.md`,
        },
        { create: true },
      );

      onCreated();
      void navigate(`/articles/${row.entry.id}/${locale}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not create the translation.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <select
        className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-xs"
        aria-label={t("articles.addTranslationAria", { title: row.localization.title })}
        value={locale}
        onChange={(event) => {
          setLocale(event.target.value);
        }}
      >
        <option value="">{t("articles.addLocaleOption")}</option>
        {missing.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
      <Button size="sm" variant="ghost" disabled={busy || locale === ""} onClick={() => void add()}>
        {t("articles.add")}
      </Button>
      {error !== null && (
        <span role="alert" className="text-xs text-red-600">
          {error}
        </span>
      )}
    </div>
  );
}
